import { useState } from "react";
import { ArrowLeftRight, Pencil, ScanSearch } from "lucide-react";
import { MaterialCenaDialog } from "@/components/cennik/MaterialCenaDialog";
import { CenaOverrideZnak } from "@/components/cennik/CenaOverrideZnak";
import { useCenaOverrideMap } from "@/api/hooks/useZakazkaCenaOverrides";
import {
  useVariantaEdgesSummary,
  useVariantaKovaniaSummary,
} from "@/api/hooks/useVarianty";
import { materialPrimaryId, menoKovania, rozmerHrany } from "@/lib/format";
import { CollapsibleSection } from "@/components/zakazka/CollapsibleSection";
import { VymenaDialog, type VymenaStary } from "@/components/zakazka/VymenaDialog";

/**
 * Sumáre hrán a kovaní — zdieľané medzi detail panelom dielca (scope
 * zostavy) a sidebar-om zákazky (scope celej varianty, zbaliteľné).
 */

function SectionShell({
  title,
  collapsible,
  children,
}: {
  title: string;
  collapsible?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  if (!collapsible) {
    return (
      <section className="px-2.5 py-1.5 space-y-1">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-primary">
          {title}
        </h3>
        <div className="space-y-1">{children}</div>
      </section>
    );
  }
  return <CollapsibleSection title={title}>{children}</CollapsibleSection>;
}

export function EdgesUsageSection({
  variantaId,
  zostavaId,
  zakazkaId,
  title = "Hrany v zákazke",
  collapsible = false,
  bezStropu = false,
  onOznac,
}: {
  variantaId: string | null;
  zostavaId?: string;
  /** Označiť v tabuľke riadky s touto hranou (KOLO 233) — len bočný panel. */
  onOznac?: (materialId: string, nazov: string) => void;
  /** Ak je zadané, riadky majú tlačidlo Vymeniť (hromadná výmena hrany). */
  zakazkaId?: string;
  title?: string;
  collapsible?: boolean;
  /**
   * Zruší vnútorný `max-h-40` scroll. V detaile dielca je strop nutný
   * (panel má pevných 260 px), v tabovom bočnom paneli by bol tretia
   * scrollovacia oblasť v už scrollovanej stránke.
   */
  bezStropu?: boolean;
}): JSX.Element {
  const query = useVariantaEdgesSummary(variantaId ?? undefined, zostavaId);
  const [cenaEdit, setCenaEdit] = useState<string | null>(null);
  // Mapa už beží kvôli cenám v detaile — nepribúda dopyt (KOLO 101)
  const overrideMap = useCenaOverrideMap(zakazkaId);
  const edges = query.data?.data?.edges ?? [];
  const [vymena, setVymena] = useState<VymenaStary | null>(null);
  return (
    <SectionShell title={`${title} (${edges.length})`} collapsible={collapsible}>
      {!variantaId ? (
        <p className="text-[11px] text-muted-foreground">
          Žiadne hrany — najprv pridaj materiály.
        </p>
      ) : query.isLoading ? (
        <p className="text-[11px] text-muted-foreground">Načítavam…</p>
      ) : edges.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          Zatiaľ žiadne hrany — pridaj dielce s materiálom zákazky.
        </p>
      ) : (
        <ul
          className={
            "space-y-0.5" + (bezStropu ? "" : " max-h-40 overflow-y-auto")
          }
        >
          {edges.map((row) => {
            const e = row.edge;
            const id = materialPrimaryId(e);
            const rozmer = rozmerHrany(e.sirka_mm, e.hrubka_mm);
            return (
              <li
                key={e.id}
                className="rounded border border-transparent hover:bg-accent px-1.5 py-0.5 text-[11px]"
                title={e.nazov}
              >
                <div className="flex items-baseline gap-1.5">
                  <span className="font-mono font-medium whitespace-nowrap">
                    {rozmer}
                  </span>
                  {id ? (
                    <span className="font-mono text-[10px] text-muted-foreground">
                      {id}
                    </span>
                  ) : null}
                  <span className="text-muted-foreground truncate text-[10px] flex-1">
                    {e.nazov}
                  </span>
                  {onOznac ? (
                    <TlacidloOznacit nazov={e.nazov} onClick={() => onOznac(e.id, e.nazov)} />
                  ) : null}
                  {zakazkaId ? (
                    <button
                      type="button"
                      aria-label={`Vymeniť hranu ${e.nazov}`}
                      title="Vymeniť hranu za inú vo všetkých dielcoch"
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
                      onClick={() =>
                        setVymena({
                          id: e.id,
                          nazov: e.nazov,
                          dekor_kod: e.dekor_kod,
                        })
                      }
                    >
                      <ArrowLeftRight className="h-3 w-3" />
                    </button>
                  ) : null}
                  {zakazkaId ? (
                    <CenaOverrideZnak
                      zakazkaId={zakazkaId}
                      materialId={e.id}
                      nazov={e.nazov}
                      upravena={overrideMap.has(e.id)}
                    />
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Upraviť ceny ${e.nazov}`}
                    title="Upraviť ceny (nákup, réžia, práca)"
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
                    onClick={() => setCenaEdit(e.id)}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">
                  {row.pocet_dielcov}× dielec · {row.pocet_stran} strán · {row.dlzka_m} m
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {cenaEdit ? (
        <MaterialCenaDialog
          materialId={cenaEdit}
          zakazkaId={zakazkaId}
          variantaId={variantaId ?? undefined}
          open
          onOpenChange={(o) => !o && setCenaEdit(null)}
        />
      ) : null}
      {vymena && zakazkaId && variantaId ? (
        <VymenaDialog
          rezim="hrana"
          zakazkaId={zakazkaId}
          variantaId={variantaId}
          zostavaId={zostavaId}
          stary={vymena}
          open
          onOpenChange={(o) => !o && setVymena(null)}
        />
      ) : null}
    </SectionShell>
  );
}

export function KovaniaUsageSection({
  variantaId,
  zostavaId,
  zakazkaId,
  title = "Kovania v zákazke",
  collapsible = false,
  bezStropu = false,
  onOznac,
}: {
  variantaId: string | null;
  zostavaId?: string;
  /** Označiť v tabuľke riadky s týmto kovaním (KOLO 233) — len bočný panel. */
  onOznac?: (materialId: string, nazov: string) => void;
  /** Ak je zadané, riadky majú tlačidlo Vymeniť (hromadná výmena kovania). */
  zakazkaId?: string;
  title?: string;
  collapsible?: boolean;
  /** Zruší vnútorný `max-h-40` scroll — pozri `EdgesUsageSection`. */
  bezStropu?: boolean;
}): JSX.Element {
  const query = useVariantaKovaniaSummary(variantaId ?? undefined, zostavaId);
  const kovania = query.data?.data?.kovania ?? [];
  const [vymena, setVymena] = useState<VymenaStary | null>(null);
  const [cenaEdit, setCenaEdit] = useState<string | null>(null);
  // Mapa už beží kvôli cenám v detaile — nepribúda dopyt (KOLO 101)
  const overrideMap = useCenaOverrideMap(zakazkaId);
  return (
    <SectionShell
      title={`${title} (${kovania.length})`}
      collapsible={collapsible}
    >
      {!variantaId || query.isLoading ? (
        <p className="text-[11px] text-muted-foreground">Načítavam…</p>
      ) : kovania.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          Zatiaľ žiadne kovania — pridaj ich cez „+ Kovanie" pri zostave.
        </p>
      ) : (
        <ul
          className={
            "space-y-0.5" + (bezStropu ? "" : " max-h-40 overflow-y-auto")
          }
        >
          {kovania.map((row) => {
            const m = row.material;
            const id = materialPrimaryId(m);
            return (
              <li
                key={m.id}
                className="rounded border border-transparent hover:bg-accent px-1.5 py-0.5 text-[11px]"
                title={m.nazov}
              >
                <div className="flex items-baseline gap-1.5">
                  <span className="font-mono font-medium whitespace-nowrap">
                    {/* Kovanie na metre (KOLO 176) — súčet dĺžok, kusy pod ním */}
                    {row.dlzka_mm != null
                      ? `${(row.dlzka_mm / 1000).toLocaleString("sk-SK", { maximumFractionDigits: 2 })} m`
                      : `${row.pocet_ks}×`}
                  </span>
                  <span className="truncate flex-1">{menoKovania(m).hlavne}</span>
                  {onOznac ? (
                    <TlacidloOznacit
                      nazov={m.nazov}
                      onClick={() => onOznac(m.id, menoKovania(m).hlavne)}
                    />
                  ) : null}
                  {zakazkaId ? (
                    <button
                      type="button"
                      aria-label={`Vymeniť kovanie ${m.nazov}`}
                      title="Vymeniť kovanie za iné vo všetkých dielcoch"
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
                      onClick={() =>
                        setVymena({
                          id: m.id,
                          nazov: m.nazov,
                          kategoria: m.kategoria,
                          alias: m.alias,
                        })
                      }
                    >
                      <ArrowLeftRight className="h-3 w-3" />
                    </button>
                  ) : null}
                  {zakazkaId ? (
                    <CenaOverrideZnak
                      zakazkaId={zakazkaId}
                      materialId={m.id}
                      nazov={m.nazov}
                      upravena={overrideMap.has(m.id)}
                    />
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Upraviť ceny ${m.nazov}`}
                    title="Upraviť ceny (nákup, réžia, práca)"
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
                    onClick={() => setCenaEdit(m.id)}
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </div>
                {menoKovania(m).pod ? (
                  <div className="truncate text-[10px] text-muted-foreground">
                    {menoKovania(m).pod}
                  </div>
                ) : null}
                {id ? (
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {id} · {row.pocet_dielcov}× dielec
                    {row.dlzka_mm != null ? ` · ${row.pocet_ks} ks` : ""}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {cenaEdit ? (
        <MaterialCenaDialog
          materialId={cenaEdit}
          zakazkaId={zakazkaId}
          variantaId={variantaId ?? undefined}
          open
          onOpenChange={(o) => !o && setCenaEdit(null)}
        />
      ) : null}
      {vymena && zakazkaId && variantaId ? (
        <VymenaDialog
          rezim="kovanie"
          zakazkaId={zakazkaId}
          variantaId={variantaId}
          zostavaId={zostavaId}
          stary={vymena}
          open
          onOpenChange={(o) => !o && setVymena(null)}
        />
      ) : null}
    </SectionShell>
  );
}

/** Označiť v tabuľke (KOLO 233) — riadky, kde sa položka nachádza. */
export function TlacidloOznacit({
  nazov,
  onClick,
}: {
  nazov: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-label={`Označiť v tabuľke ${nazov}`}
      title="Označiť v tabuľke — všetky riadky, kde sa nachádza"
      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
      onClick={onClick}
    >
      <ScanSearch className="h-3 w-3" />
    </button>
  );
}
